import type { apiClient } from '~/shared/api/data-ingestion-api'
import { centsToDollars, formatCurrency } from '~/shared/lib/currency'
import { formatDateDefault } from '~/shared/lib/datetime'
import type { AuditTrailEventDescriptionField, EobPaymentDepositEventProps } from '../../model'
import type { EobAuditEvent } from '../eob-types'
import { asEobEvent, findPreviousPaymentSummaryEvent } from '../eob-types'
import { collectFieldSearchText } from './searchable-text-utils'

/** Payment type enum → display label */
const PAYMENT_TYPE_LABELS: Record<string, string> = {
  check: 'Check',
  direct_deposit: 'Direct Deposit',
  credit_card: 'Credit Card',
}

/** Field config for diffing PaymentSummaryResponse scalar fields */
interface PaymentFieldConfig {
  key: keyof apiClient.PaymentSummaryResponse
  label: string
  format: (value: unknown) => string | null
}

const PAYMENT_SUMMARY_FIELDS: PaymentFieldConfig[] = [
  {
    key: 'payment_type',
    label: 'Payment Type',
    format: (v) => (typeof v === 'string' ? (PAYMENT_TYPE_LABELS[v] ?? v) : null),
  },
  {
    key: 'total_paid',
    label: 'Total Paid',
    format: (v) => {
      const dollars = centsToDollars(v as number | string | null | undefined)
      return dollars !== null ? formatCurrency(dollars) : null
    },
  },
  {
    key: 'check_date',
    label: 'Check Date',
    format: (v) => (typeof v === 'string' ? formatDateDefault(v) : null),
  },
  {
    key: 'deposit_date',
    label: 'Deposit Date',
    format: (v) => (typeof v === 'string' ? formatDateDefault(v) : null),
  },
  {
    key: 'check_number',
    label: 'Check Number',
    format: (v) => (typeof v === 'string' ? v : null),
  },
  {
    key: 'ach_trace_number',
    label: 'ACH Trace Number',
    format: (v) => (typeof v === 'string' ? v : null),
  },
  {
    key: 'bank_reference_number',
    label: 'Bank Reference Number',
    format: (v) => (typeof v === 'string' ? v : null),
  },
  {
    key: 'epc_draft_number',
    label: 'EPC Draft Number',
    format: (v) => (typeof v === 'string' ? v : null),
  },
  {
    key: 'tax_id',
    label: 'Tax ID',
    format: (v) => (typeof v === 'string' ? v : null),
  },
  {
    key: 'customer_service_number',
    label: 'Customer Service Number',
    format: (v) => (typeof v === 'string' ? v : null),
  },
  {
    key: 'number_of_patient_claims',
    label: 'Number of Patient Claims',
    format: (v) => (typeof v === 'number' ? String(v) : null),
  },
]

/** Check if there is a previous eob_payment_deposit event (for title logic only). */
function hasPreviousPaymentDepositEvent(
  filteredEvents: apiClient.AuditEvent[],
  index: number,
): boolean {
  for (let i = index - 1; i >= 0; i--) {
    const e = filteredEvents[i]
    if (e.event_type === 'eob_payment_deposit') {
      const eob = asEobEvent(e)
      if (eob.payment_summary) return true
    }
  }
  return false
}

/**
 * Build diff fields for payment deposit.
 * First event: show all non-null fields as saved values.
 * Subsequent events: show only changed fields with previous -> current.
 */
function buildPaymentDepositFields(
  current: EobAuditEvent,
  previous: EobAuditEvent | null,
): AuditTrailEventDescriptionField[] | null {
  const currentSummary = current.payment_summary
  if (!currentSummary) return null

  const previousSummary = previous?.payment_summary
  const fields: AuditTrailEventDescriptionField[] = []

  for (const config of PAYMENT_SUMMARY_FIELDS) {
    const currentRaw = currentSummary[config.key]
    const currentFormatted = config.format(currentRaw)

    if (!previousSummary) {
      // First event: show non-null fields the user saved
      if (currentFormatted !== null) {
        fields.push({
          label: config.label,
          values: [{ diff: [currentFormatted] }],
        })
      }
    } else {
      // Subsequent event: show only changed fields
      const prevRaw = previousSummary[config.key]
      const prevFormatted = config.format(prevRaw)

      if (currentFormatted !== prevFormatted) {
        fields.push({
          label: config.label,
          values: [
            {
              previous: prevFormatted ?? '--',
              current: currentFormatted ?? '--',
            },
          ],
        })
      }
    }
  }

  return fields.length > 0 ? fields : null
}

export function transformEobPaymentDeposit(
  event: apiClient.AuditEvent,
  filteredEvents: apiClient.AuditEvent[],
  index: number,
): EobPaymentDepositEventProps {
  const eobEvent = asEobEvent(event)
  const previousEvent = findPreviousPaymentSummaryEvent(filteredEvents, index)
  const isRevalidation = hasPreviousPaymentDepositEvent(filteredEvents, index)

  // Determine title
  const depositSuffix = eobEvent.payment_summary?.receipt_confirmed
    ? 'Deposit Received'
    : 'Deposit Not Received'

  const title = !isRevalidation
    ? `Payment Deposit: Fields Validated – ${depositSuffix}`
    : `Payment Deposit: Fields Revalidated – ${depositSuffix}`

  const notes = event.notes ?? null

  const fields = buildPaymentDepositFields(eobEvent, previousEvent)

  return {
    type: 'eob-payment-deposit',
    timestamp: event.timestamp,
    user: event.user,
    userRole: event.user ? 'person' : 'opti',
    step: 'eob-payment-deposit',
    eventType: event.event_type,
    title,
    fields,
    notes,
  }
}

export function getEobPaymentDepositSearchableText(props: EobPaymentDepositEventProps): string {
  const parts = [props.title]
  if (props.fields) parts.push(collectFieldSearchText(props.fields))
  if (props.notes) parts.push(props.notes)
  return parts.join(' ')
}
